import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
// S6 — Reconciliation workspace (3-column layout).
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Play, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader, Table } from '~/components/data';
import { AIInsightCard, HeroMetric, NewRunModal } from '~/components/domain';
import { Button, DateInput } from '~/components/primitives';
import { StatusBadge } from '~/components/status';
import { fetchReconciliationWorkspace } from '~/lib/api/workspaces';
import shared from './_auth._shared.module.css';
import styles from './_auth.reconciliation.module.css';

export const Route = createFileRoute('/_auth/reconciliation')({
  component: ReconciliationWorkspace,
});

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function ReconciliationWorkspace(): JSX.Element {
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [newRunOpen, setNewRunOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['reconciliation', 'workspace', period],
    queryFn: () => fetchReconciliationWorkspace(period),
    staleTime: 30_000,
  });

  // FE-243 Pass 5: intro card explainer для двух tracks (agent draft + manual).
  // Sticky до первого run или первого manual document — onboarding guidance.
  const hasAnyData = Boolean(data?.last_run || data?.pending_documents?.length);

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_reconciliation()}
        description={m.route_recon_description()}
        actions={
          <div className={shared.actions}>
            <Button
              variant="secondary"
              leadingIcon={<Play size={14} />}
              onClick={() => setNewRunOpen(true)}
            >
              {m.route_recon_btn_run()}
            </Button>
            <Button
              leadingIcon={<Plus size={14} />}
              onClick={() => navigate({ to: '/stock-reconciliation' })}
            >
              {m.route_recon_btn_create_doc()}
            </Button>
          </div>
        }
      />

      {!hasAnyData && !isLoading && (
        <div className={styles.introCard} role="region" aria-label={m.route_recon_intro_aria()}>
          <h2 className={styles.introHeading}>{m.route_recon_intro_heading()}</h2>
          <div className={styles.introTracks}>
            <div className={styles.introTrack}>
              <strong>{m.route_recon_intro_track_agent_title()}</strong>
              <p>{m.route_recon_intro_track_agent_body()}</p>
            </div>
            <div className={styles.introTrack}>
              <strong>{m.route_recon_intro_track_manual_title()}</strong>
              <p>{m.route_recon_intro_track_manual_body()}</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.filters}>
        <div className={styles.periodField}>
          <DateInput
            label={m.route_recon_filter_period()}
            mode="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value || currentPeriod())}
          />
        </div>
        {data?.last_run && (
          <Link
            to="/runs/$runId"
            params={{ runId: data.last_run.run_id }}
            className={styles.lastRunChip}
          >
            <span className={shared.mono}>{data.last_run.run_id.slice(0, 8)}</span>
            {data.last_run.business_status && (
              <StatusBadge status={data.last_run.business_status} />
            )}
          </Link>
        )}
      </div>

      <div className={styles.heroRow}>
        <HeroMetric
          label={m.route_recon_metric_items()}
          value={
            data?.balance_summary?.total_items ??
            data?.stock_balance_summary.items ??
            (isLoading ? '…' : 0)
          }
        />
        <HeroMetric
          label={m.route_recon_metric_value()}
          value={
            data?.balance_summary?.total_value ?? data?.stock_balance_summary.total_value ?? '—'
          }
          compact
        />
        <HeroMetric
          label={m.route_recon_metric_variance()}
          value={data?.balance_summary?.variance_lines ?? (isLoading ? '…' : 0)}
        />
      </div>

      <div className={styles.grid}>
        <section aria-label={m.route_recon_agent_col_aria()} className={styles.agentCol}>
          {data?.last_run?.agent_draft ? (
            <AIInsightCard
              title={m.route_recon_agent_draft_title()}
              body={
                <pre className={shared.jsonPre}>
                  {JSON.stringify(data.last_run.agent_draft, null, 2)}
                </pre>
              }
            />
          ) : (
            <EmptyState
              title={m.route_recon_agent_empty_title()}
              description={m.route_recon_agent_empty_description()}
            />
          )}
        </section>

        <section aria-label={m.route_recon_data_col_aria()} className={styles.dataCol}>
          <h2 className={shared.sectionTitle}>{m.route_recon_data_heading()}</h2>
          <Table
            ariaLabel={m.route_recon_data_col_aria()}
            loading={isLoading}
            data={data?.pending_documents ?? []}
            rowKey={(r) => r.id}
            emptyTitle={m.route_recon_data_empty_title()}
            emptyDescription={m.route_recon_data_empty_description()}
            columns={[
              {
                id: 'id',
                header: m.common_column_id(),
                accessor: (r) => r.id.slice(0, 8),
                mono: true,
              },
              {
                id: 'wh',
                header: m.route_recon_data_col_wh(),
                accessor: (r) => r.warehouse_label,
              },
              {
                id: 'lines',
                header: m.route_recon_data_col_lines(),
                accessor: (r) => r.lines_count,
                align: 'right',
                mono: true,
              },
              {
                id: 'var',
                header: m.route_recon_data_col_variance(),
                accessor: (r) => r.variance_value ?? '—',
                align: 'right',
                mono: true,
              },
              {
                id: 'status',
                header: m.route_recon_data_col_status(),
                accessor: (r) => r.status,
                mono: true,
              },
            ]}
          />
        </section>

        <aside aria-label={m.route_recon_actions_col_aria()} className={styles.actionsCol}>
          <h2 className={shared.sectionTitle}>{m.route_recon_actions_heading()}</h2>
          <div className={styles.actionList}>
            <Button
              variant="primary"
              leadingIcon={<Plus size={14} />}
              onClick={() => navigate({ to: '/stock-reconciliation' })}
            >
              {m.route_recon_action_create_doc()}
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<Play size={14} />}
              onClick={() => setNewRunOpen(true)}
            >
              {m.route_recon_action_new_run()}
            </Button>
            <Link to="/conflicts" className={shared.link}>
              {m.route_recon_action_conflicts()}
            </Link>
            <Link to="/anomalies" className={shared.link}>
              {m.route_recon_action_anomalies()}
            </Link>
          </div>
        </aside>
      </div>

      <NewRunModal open={newRunOpen} onClose={() => setNewRunOpen(false)} />
    </div>
  );
}
